import type { Locale } from "@/api/types";
import { catalogs, type MessageKey } from "./catalogs";

export type MessageParameters = Record<string, string | number>;

const languageTags: Record<Locale, string> = { en: "en-US", id: "id-ID" };

function interpolate(template: string, parameters: MessageParameters): string {
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = parameters[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(
  locale: Locale,
  key: MessageKey,
  parameters: MessageParameters = {},
): string {
  const message = catalogs[locale][key];
  const template = typeof message === "string"
    ? message
    : message[new Intl.PluralRules(languageTags[locale]).select(Number(parameters.count)) === "one" ? "one" : "other"];
  return interpolate(template, parameters);
}

export function formatDateTime(
  value: string | number | Date,
  locale: Locale,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const componentOptions: Array<keyof Intl.DateTimeFormatOptions> = [
    "weekday",
    "era",
    "year",
    "month",
    "day",
    "dayPeriod",
    "hour",
    "minute",
    "second",
    "fractionalSecondDigits",
    "timeZoneName",
  ];
  const usesComponents = componentOptions.some((key) => options[key] !== undefined);
  const usesStyles = options.dateStyle !== undefined || options.timeStyle !== undefined;

  return new Intl.DateTimeFormat(languageTags[locale], {
    timeZone: timezone,
    ...(usesComponents || usesStyles ? {} : { dateStyle: "medium", timeStyle: "short" }),
    ...options,
  }).format(new Date(value));
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(languageTags[locale]).format(value);
}

export function formatAmount(value: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(languageTags[locale], {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
  }).format(value);
}
